import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';

import { tabelas, buscarNcm, buscarCest } from '../tributosApi';
import { validarRegrasTributarias } from '../regrasTributarias';

// Cache simples das tabelas auxiliares (carregadas uma vez por sessão)
let cacheTabelas = null;
let promessaTabelas = null;

const paraOpcoes = (lista, numerico = false) => (lista || [])
  .slice()
  .sort((a, b) => String(a.codigo).localeCompare(String(b.codigo), 'pt-BR', { numeric: true }))
  .map(i => ({
    label: `${String(i.codigo).trim()} - ${String(i.descricao || '').trim()}`,
    value: numerico ? Number(i.codigo) : String(i.codigo).trim(),
  }));

function carregarTabelas() {
  if (cacheTabelas) return Promise.resolve(cacheTabelas);
  if (!promessaTabelas) {
    const seguro = p => p.catch(() => []);
    promessaTabelas = Promise.all([
      seguro(tabelas.cst()), seguro(tabelas.csosn()), seguro(tabelas.pisCofins()), seguro(tabelas.origem()),
      seguro(tabelas.modalidadeBc()), seguro(tabelas.modalidadeBcSt()), seguro(tabelas.classTribIBSCBS()),
      seguro(tabelas.classTribIS()),
    ]).then(([cst, csosn, pisCofins, origem, modBc, modBcSt, ctIbsCbs, ctIs]) => {
      cacheTabelas = {
        cst: paraOpcoes(cst),
        csosn: paraOpcoes(csosn),
        pisCofins: paraOpcoes(pisCofins),
        origem: origem.length ? paraOpcoes(origem, true) : [
          { label: '0 - Nacional', value: 0 },
          { label: '1 - Estrangeira - Importação direta', value: 1 },
          { label: '2 - Estrangeira - Adquirida no mercado interno', value: 2 },
        ],
        modalidadeBc: modBc.length ? paraOpcoes(modBc, true) : [
          { label: '0 - Margem Valor Agregado (%)', value: 0 },
          { label: '1 - Pauta (Valor)', value: 1 },
          { label: '2 - Preço Tabelado Máx.', value: 2 },
          { label: '3 - Valor da Operação', value: 3 },
        ],
        modalidadeBcSt: paraOpcoes(modBcSt, true),
        classTribIBSCBS: paraOpcoes(ctIbsCbs),
        classTribIS: paraOpcoes(ctIs),
      };
      return cacheTabelas;
    }).finally(() => { promessaTabelas = null; });
  }
  return promessaTabelas;
}

// CST PIS/COFINS: entrada = 50 a 99; saída = 01 a 49 e 99
const cstPisCofinsEntrada = op => { const n = Number(op.value); return n >= 50 && n <= 99; };
const cstPisCofinsSaida = op => { const n = Number(op.value); return (n >= 1 && n <= 49) || n === 99; };

function Campo({ label, children, span = 3, hint, alerta, erro, aviso }) {
  let rodape = null;
  if (erro) {
    rodape = <small className="tb-hint tb-hint-erro" title={erro}><i className="pi pi-times-circle" /> {erro}</small>;
  } else if (aviso) {
    rodape = <small className="tb-hint tb-hint-alerta tb-hint-regra" title={aviso}><i className="pi pi-exclamation-triangle" /> {aviso}</small>;
  } else if (hint) {
    rodape = <small className={`tb-hint ${alerta ? 'tb-hint-alerta' : ''}`}>{alerta ? <i className="pi pi-exclamation-triangle" /> : null} {hint}</small>;
  }
  return (
    <div className={`tb-field tb-span-${span} ${erro ? 'tb-field-erro' : ''}`}>
      <label>{label}</label>
      {children}
      {rodape}
    </div>
  );
}

Campo.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  span: PropTypes.number,
  hint: PropTypes.string,
  alerta: PropTypes.bool,
  erro: PropTypes.string,
  aviso: PropTypes.string,
};

export default function FormTributos({
  valor, onChange, bloquearEan, referenciaNota, modoNcm, ocultarIdentificacao, ncmCestDoProduto,
}) {
  const [opcoes, setOpcoes] = useState(cacheTabelas || {
    cst: [], csosn: [], pisCofins: [], origem: [], modalidadeBc: [], modalidadeBcSt: [], classTribIBSCBS: [], classTribIS: [],
  });
  const [sugestoesNcm, setSugestoesNcm] = useState([]);
  const [sugestoesCest, setSugestoesCest] = useState([]);

  useEffect(() => {
    let ativo = true;
    carregarTabelas().then(t => { if (ativo) setOpcoes(t); });
    return () => { ativo = false; };
  }, []);

  // Regras tributárias (CEST x ST, CFOP x CST/CSOSN, alíquotas por CST, PIS/COFINS...)
  const regras = useMemo(() => {
    const res = validarRegrasTributarias(valor);
    // Lote mantendo NCM/CEST de cada produto: o CEST é validado por produto no backend
    if (!ncmCestDoProduto) return res;
    return { erros: res.erros.filter(e => e.campo !== 'cest'), avisos: res.avisos.filter(a => a.campo !== 'cest') };
  }, [valor, ncmCestDoProduto]);
  const r = campo => {
    const e = regras.erros.find(i => i.campo === campo);
    const a = regras.avisos.find(i => i.campo === campo);
    return { erro: e ? e.mensagem : undefined, aviso: a ? a.mensagem : undefined };
  };

  const set = (campo, v) => onChange({ ...valor, [campo]: v });
  const setVarios = obj => onChange({ ...valor, ...obj });

  const texto = (campo, props = {}) => (
    <InputText
      value={valor[campo] || ''}
      onChange={e => set(campo, props.numerico ? e.target.value.replace(/\D/g, '') : e.target.value)}
      maxLength={props.maxLength}
      disabled={props.disabled}
      placeholder={props.placeholder}
      className={props.className}
    />
  );

  const numero = (campo, casas = 2, sufixo = ' %') => (
    <InputNumber
      value={valor[campo] === '' ? null : valor[campo]}
      onValueChange={e => set(campo, e.value === null ? 0 : e.value)}
      mode="decimal"
      locale="pt-BR"
      minFractionDigits={casas}
      maxFractionDigits={casas}
      min={0}
      suffix={sufixo}
      inputClassName="tb-num"
    />
  );

  const lista = (campo, itens, props = {}) => (
    <Dropdown
      value={valor[campo] === '' ? null : valor[campo]}
      options={itens}
      onChange={e => set(campo, e.value === undefined || e.value === null ? '' : e.value)}
      filter={itens.length > 8}
      showClear={props.showClear !== false}
      placeholder={props.placeholder || 'Selecione'}
      emptyMessage="Nenhum registro"
      emptyFilterMessage="Nenhum registro"
      className="tb-dropdown"
      panelClassName="tb-dropdown-panel"
    />
  );

  const completarNcm = e => {
    const q = String(e.query || '').replace(/\D/g, '');
    if (q.length < 2) { setSugestoesNcm([]); return; }
    buscarNcm(q).then(l => setSugestoesNcm(l.slice(0, 40))).catch(() => setSugestoesNcm([]));
  };

  const completarCest = e => {
    const q = String(e.query || '').replace(/\D/g, '');
    if (q.length < 2) { setSugestoesCest([]); return; }
    buscarCest(q).then(l => setSugestoesCest(l.slice(0, 40))).catch(() => setSugestoesCest([]));
  };

  const itemTemplate = item => (
    <div className="tb-ac-item"><strong>{item.codigo}</strong><span>{item.descricao}</span></div>
  );

  const selecionarNcm = item => {
    const classTrib = item.cClassTribIBSCBS || item.cclassTribIBSCBS;
    const extra = {};
    if (classTrib && !valor.cClassTribIBSCBS) extra.cClassTribIBSCBS = String(classTrib).trim();
    setVarios({ ncm: String(item.codigo).trim(), ...extra });
  };

  const notaDiferente = (campo, campoNota) => referenciaNota
    && referenciaNota[campoNota]
    && valor[campo]
    && String(referenciaNota[campoNota]).trim() !== String(valor[campo]).trim();

  return (
    <div className="tb-form">
      {regras.erros.length || regras.avisos.length ? (
        <section className={`tb-regras ${regras.erros.length ? 'tb-regras-erro' : 'tb-regras-aviso'}`}>
          <header>
            <i className={regras.erros.length ? 'pi pi-times-circle' : 'pi pi-exclamation-triangle'} />
            {regras.erros.length
              ? ` ${regras.erros.length} inconsistência(s) tributária(s) — corrija antes de conferir`
              : ` ${regras.avisos.length} aviso(s) tributário(s)`}
          </header>
          <ul>
            {regras.erros.map(e => <li key={`e${e.mensagem}`} className="tb-regra-erro">{e.mensagem}</li>)}
            {regras.avisos.map(a => <li key={`a${a.mensagem}`} className="tb-regra-aviso">{a.mensagem}</li>)}
          </ul>
        </section>
      ) : null}
      {!modoNcm && !ocultarIdentificacao ? (
      <section className="tb-section">
        <header><i className="pi pi-tag" /> Identificação</header>
        <div className="tb-grid">
          <Campo label="Código EAN" span={3}>
            {texto('codigoEan', { maxLength: 20, disabled: bloquearEan, numerico: false })}
          </Campo>
          <Campo label="Descrição" span={9}>
            {texto('descricao', { maxLength: 120 })}
          </Campo>
          {!valor.codigoEan ? (
            <>
              <Campo label="CNPJ do cliente" span={4} hint="Produto sem EAN: obrigatório">
                {texto('cnpjCliente', { maxLength: 20, numerico: true, disabled: bloquearEan, className: 'tb-mono' })}
              </Campo>
              <Campo label="Registro do produto" span={3} hint="Código na Retaguarda">
                <InputNumber
                  value={valor.registroProduto || null}
                  onValueChange={e => set('registroProduto', e.value)}
                  useGrouping={false}
                  min={1}
                  disabled={bloquearEan}
                  inputClassName="tb-mono"
                />
              </Campo>
            </>
          ) : null}
        </div>
      </section>
      ) : null}

      <section className="tb-section">
        <header><i className="pi pi-sitemap" /> Classificação fiscal</header>
        <div className="tb-grid">
          <Campo label="NCM" span={3} alerta hint={notaDiferente('ncm', 'ncm') ? `Na NF: ${referenciaNota.ncm}` : undefined}>
            {ncmCestDoProduto ? <InputText value="" placeholder="De cada produto" disabled /> : null}
            {!ncmCestDoProduto && modoNcm ? <InputText value={valor.ncm || ''} disabled className="tb-mono" /> : null}
            {ncmCestDoProduto || modoNcm ? null : (
            <AutoComplete
              value={valor.ncm || ''}
              suggestions={sugestoesNcm}
              completeMethod={completarNcm}
              field="codigo"
              itemTemplate={itemTemplate}
              onChange={e => set('ncm', typeof e.value === 'string' ? e.value.replace(/\D/g, '').substring(0, 8) : valor.ncm)}
              onSelect={e => selecionarNcm(e.value)}
              placeholder="8 dígitos"
              inputClassName="tb-mono"
              delay={350}
            />
            )}
          </Campo>
          <Campo label="CEST" span={3} alerta hint={notaDiferente('cest', 'cest') ? `Na NF: ${referenciaNota.cest}` : undefined} {...r('cest')}>
            {ncmCestDoProduto ? <InputText value="" placeholder="De cada produto" disabled /> : (
            <AutoComplete
              value={valor.cest || ''}
              suggestions={sugestoesCest}
              completeMethod={completarCest}
              field="codigo"
              itemTemplate={itemTemplate}
              onChange={e => set('cest', typeof e.value === 'string' ? e.value.replace(/\D/g, '').substring(0, 7) : valor.cest)}
              onSelect={e => set('cest', String(e.value.codigo).trim())}
              placeholder="7 dígitos"
              inputClassName="tb-mono"
              delay={350}
            />
            )}
          </Campo>
          <Campo label="Origem da mercadoria" span={4}>
            {lista('origemMercadoria', opcoes.origem, { showClear: false })}
          </Campo>
          <Campo label="CFOP venda" span={2} hint="Ex.: 102 (5102/6102)" {...r('cfopVenda')}>
            {texto('cfopVenda', { maxLength: 3, numerico: true, className: 'tb-mono' })}
          </Campo>
        </div>
      </section>

      <section className="tb-section">
        <header><i className="pi pi-percentage" /> ICMS</header>
        <div className="tb-grid">
          <Campo label="CST (Regime normal)" span={6}>{lista('cstVenda', opcoes.cst)}</Campo>
          <Campo label="CSOSN (Simples Nacional)" span={6}>{lista('csosnVenda', opcoes.csosn)}</Campo>
          <Campo label="Modalidade da BC" span={4}>{lista('icmsModalidadeBc', opcoes.modalidadeBc, { showClear: false })}</Campo>
          <Campo label="Alíquota ICMS" span={3} {...r('icmsAliquota')}>{numero('icmsAliquota')}</Campo>
          <Campo label="Redução da BC" span={3} {...r('icmsPRedBc')}>{numero('icmsPRedBc')}</Campo>
          <Campo label="Alíquota FCP" span={2} {...r('icmsAliquotaFcp')}>{numero('icmsAliquotaFcp')}</Campo>
        </div>
        <div className="tb-subtitle">Substituição tributária</div>
        <div className="tb-grid">
          <Campo label="Modalidade da BC ST" span={4}>{lista('icmsModalidadeBcSt', opcoes.modalidadeBcSt, { showClear: false })}</Campo>
          <Campo label="Alíquota ICMS ST" span={2} {...r('icmsAliquotaSt')}>{numero('icmsAliquotaSt')}</Campo>
          <Campo label="Redução BC ST" span={2}>{numero('icmsPRedBcSt')}</Campo>
          <Campo label="MVA" span={2} {...r('mva')}>{numero('mva')}</Campo>
          <Campo label="MVA ST" span={2}>{numero('mvaSt')}</Campo>
        </div>
      </section>

      <section className="tb-section">
        <header><i className="pi pi-wallet" /> PIS / COFINS</header>
        <div className="tb-grid">
          <Campo label="CST entrada" span={6}>{lista('pisCofinsEntradaCst', opcoes.pisCofins.filter(cstPisCofinsEntrada))}</Campo>
          <Campo label="PIS entrada" span={3} {...r('pisEntradaAliquota')}>{numero('pisEntradaAliquota', 4)}</Campo>
          <Campo label="COFINS entrada" span={3}>{numero('cofinsEntradaAliquota', 4)}</Campo>
          <Campo label="CST saída" span={6}>{lista('pisCofinsSaidaCst', opcoes.pisCofins.filter(cstPisCofinsSaida))}</Campo>
          <Campo label="PIS saída" span={2} {...r('pisSaidaAliquota')}>{numero('pisSaidaAliquota', 4)}</Campo>
          <Campo label="COFINS saída" span={2}>{numero('cofinsSaidaAliquota', 4)}</Campo>
          <Campo label="Natureza receita" span={2} {...r('naturezaReceita')}>{texto('naturezaReceita', { maxLength: 3, numerico: true, className: 'tb-mono' })}</Campo>
        </div>
      </section>

      <section className="tb-section">
        <header><i className="pi pi-briefcase" /> Reforma tributária — IBS / CBS / IS</header>
        <div className="tb-grid">
          <Campo label="cClassTrib IBS/CBS" span={6} {...r('cClassTribIBSCBS')}>{lista('cClassTribIBSCBS', opcoes.classTribIBSCBS)}</Campo>
          <Campo label="cClassTrib IBS/CBS regular" span={6} {...r('cClassTribIBSCBSRegular')}>{lista('cClassTribIBSCBSRegular', opcoes.classTribIBSCBS)}</Campo>
          <Campo label="cClassTrib IS" span={6} {...r('cClassTribIS')}>{lista('cClassTribIS', opcoes.classTribIS)}</Campo>
          <Campo label="Cód. alíquotas créd. presumido" span={6}>{texto('codigoAliquotasCredPresIBSCBS', { maxLength: 10, className: 'tb-mono' })}</Campo>
          <Campo label="Alíq. ad rem IBS" span={6}>{numero('aliquotaAdRemIBS', 4, '')}</Campo>
          <Campo label="Alíq. ad rem CBS" span={6}>{numero('aliquotaAdRemCBS', 4, '')}</Campo>
          <Campo label="Alíq. ad valorem IBS UF" span={4}>{numero('aliquotaAdValoremIBSUF', 4)}</Campo>
          <Campo label="Alíq. ad valorem IBS Mun." span={4}>{numero('aliquotaAdValoremIBSMun', 4)}</Campo>
          <Campo label="Alíq. ad valorem CBS" span={4}>{numero('aliquotaAdValoremCBS', 4)}</Campo>
          <Campo label="Diferimento IBS UF" span={4}>{numero('pDifIBSUF', 4)}</Campo>
          <Campo label="Diferimento IBS Mun." span={4}>{numero('pDifIBSMun', 4)}</Campo>
          <Campo label="Diferimento CBS" span={4}>{numero('pDifCBS', 4)}</Campo>
        </div>
      </section>
    </div>
  );
}

FormTributos.propTypes = {
  valor: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  bloquearEan: PropTypes.bool,
  modoNcm: PropTypes.bool,
  ocultarIdentificacao: PropTypes.bool,
  ncmCestDoProduto: PropTypes.bool,
  referenciaNota: PropTypes.shape({
    ncm: PropTypes.string,
    cest: PropTypes.string,
  }),
};

FormTributos.defaultProps = {
  bloquearEan: false,
  modoNcm: false,
  ocultarIdentificacao: false,
  ncmCestDoProduto: false,
  referenciaNota: null,
};
